import { authenticatedFetch } from '@shopify/app-bridge/utilities';
import { getAppBridge, isEmbedded, getShopDomain, navigateToOAuth } from './appBridge';

/**
 * Fetch with automatic session token authentication
 * Uses App Bridge authenticatedFetch for embedded apps, falls back to regular fetch for non-embedded
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  let response: Response;
  
  // Use authenticatedFetch if embedded (automatically handles session tokens)
  if (isEmbedded()) {
    const app = getAppBridge();
    if (app) {
      try {
        // authenticatedFetch automatically attaches session token as Authorization: Bearer <token>
        const fetchFunction = authenticatedFetch(app);
        console.log('[fetchWithAuth] Using authenticatedFetch for embedded app');
        response = await fetchFunction(url, options);
      } catch (error) {
        console.error('[fetchWithAuth] Failed to use authenticatedFetch:', error);
        // Fall through to regular fetch below
        response = await fetch(url, options);
      }
    } else {
      // No App Bridge instance, use regular fetch
      response = await fetch(url, options);
    }
  } else {
    // Non-embedded context - use regular fetch
    response = await fetch(url, options);
  }
  
  // Check for 401 regardless of which fetch method was used
  if (response.status === 401) {
    // Session expired or invalid - trigger re-auth
    // Backend has determined session is invalid (server-authoritative)
    // Try to get shop from URL param or host parameter
    const shop = getShopDomain();
    
    if (shop) {
      // Determine return path based on current location
      const currentPath = window.location.pathname;
      const isAdminPath = currentPath.includes('/admin') || currentPath === '/';
      const returnPath = isAdminPath ? '/admin' : currentPath;
      
      console.log('[fetchWithAuth] Session expired (401), redirecting to OAuth using App Bridge Redirect');
      console.log('[fetchWithAuth] Shop:', shop);
      console.log('[fetchWithAuth] Return path:', returnPath);
      
      // Use App Bridge Redirect for embedded apps (correct way)
      // This ensures proper top-level navigation without iframe security issues
      navigateToOAuth(shop, returnPath);
      
      // Return a promise that never resolves (we're redirecting anyway)
      return new Promise(() => {});
    } else {
      console.error('[fetchWithAuth] 401 error but could not determine shop from URL or host parameter');
    }
  }
  
  return response;
}

/**
 * JSON fetch with automatic session refresh
 */
export async function fetchJsonWithAuth<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetchWithAuth(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    // Try to get error details from response
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      // Attach full error data for debugging
      (errorMessage as any).errorData = errorData;
    } catch (e) {
      // If response is not JSON, use status text
      try {
        const text = await response.text();
        if (text) {
          errorMessage = text.substring(0, 200); // Limit length
        }
      } catch (e2) {
        // Ignore
      }
    }
    const error = new Error(errorMessage);
    (error as any).status = response.status;
    (error as any).response = response;
    throw error;
  }
  
  return response.json();
}
